import { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, List, ListItem, ListItemText, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { useWebAuthnCredentials } from "../../../api/webauthn";
import { WebAuthnCredentialData } from "../../../types/auth";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import { RegisterPasskeyForm } from "./registerPasskeyForm.component";

export const ManagePasskeys = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading: listLoading, list, remove } = useWebAuthnCredentials();

  const [credentials, setCredentials] = useState<WebAuthnCredentialData[]>([]);

  const refreshCredentials = async () => setCredentials(await list());

  useEffect(() => {
    refreshCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemove = async (id: number) => {
    const { success } = await remove(id);
    if (success) {
      await refreshCredentials();
      enqueueSnackbar(t("Passkey removed"), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("Failed to remove passkey"), ERROR_SNACKBAR);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {listLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : credentials.length > 0 ? (
        <List dense>
          {credentials.map((credential) => (
            <ListItem
              key={credential.id}
              secondaryAction={
                <IconButton edge="end" onClick={() => handleRemove(credential.id)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText
                primary={credential.device_name || t("Passkey")}
                secondary={
                  credential.last_used_at
                    ? t("Last used {{date}}", { date: dayjs(credential.last_used_at).format("YYYY-MM-DD HH:mm") })
                    : t("Registered {{date}}", { date: dayjs(credential.created_at).format("YYYY-MM-DD HH:mm") })
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("You have no passkeys registered yet.")}
        </Typography>
      )}

      <RegisterPasskeyForm onSuccess={refreshCredentials} />
    </Box>
  );
};
